import { Button } from '@/components/ui/button';
import { CheckCircle2, ChevronDown, CircleAlert, Loader2, MailCheck, QrCode } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import { GetClientMchdItem, MchdStatus, sendMchdInvite } from '../../../../api';
import { getErrorMessage } from '@/shared/lib/getErrorMessage';
import { MchdCardProps } from './types';
import { toast } from '@/components/ui/sonner';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

const MCHD_EXPIRES_SOON_DAYS = 14;
const RESEND_DELAY_SECONDS = 60;

const getDateOnlyTime = (date: Date): number => {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
};

const getDaysUntilMchdExpires = (date: string): number => {
  const [year, month, day] = date.split('-').map(Number);
  const endDateTime = Date.UTC(year, month - 1, day);

  if (Number.isNaN(endDateTime)) return Number.POSITIVE_INFINITY;

  return (endDateTime - getDateOnlyTime(new Date())) / 86_400_000;
};

const getIsMchdExpiresSoon = (date: string): boolean => {
  const daysUntilMchdExpires = getDaysUntilMchdExpires(date);

  return daysUntilMchdExpires >= 0 && daysUntilMchdExpires <= MCHD_EXPIRES_SOON_DAYS;
};

const getMchdDateTime = (date: string): number => {
  const time = new Date(`${date}T23:59:59`).getTime();

  if (Number.isNaN(time)) return 0;

  return time;
};

const getLatestMchdByEndDate = (items: GetClientMchdItem[]): GetClientMchdItem | undefined => {
  return [...items]
    .sort((firstItem, secondItem) => getMchdDateTime(secondItem.end_date) - getMchdDateTime(firstItem.end_date))[0];
};

const getVisibleMchdItems = (items: GetClientMchdItem[]): GetClientMchdItem[] => {
  const activeMchd = getLatestMchdByEndDate(items.filter((item) => item.status === MchdStatus.Active));

  if (activeMchd) return [activeMchd];

  const inactiveMchd = getLatestMchdByEndDate(
    items.filter((item) => item.status === MchdStatus.Expired || item.status === MchdStatus.Revoked)
  );

  if (inactiveMchd) return [inactiveMchd];

  return [];
};

const MchdCard = (props: MchdCardProps) => {
  const {
    email,
    isLoading,
    items,
    orgId,
    requiresMchd
  } = props;

  const [isActiveDetailsOpen, setIsActiveDetailsOpen] = useState<boolean>(true);
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [isInstructionSent, setIsInstructionSent] = useState<boolean>(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState<number>(0);

  const location = useLocation();

  const mchdItems = items ?? [];
  const visibleMchdItems = getVisibleMchdItems(mchdItems);
  const singleMchd = visibleMchdItems[0];
  const isMchdExpired = singleMchd?.status === MchdStatus.Expired;
  const isMchdExpiresSoon = singleMchd?.status === MchdStatus.Active && getIsMchdExpiresSoon(singleMchd.end_date);
  const isMchdRevoked = singleMchd?.status === MchdStatus.Revoked;
  const shouldRenderInstructionBlock =
    Boolean(singleMchd) && (isMchdExpired || isMchdExpiresSoon || isMchdRevoked);
  const shouldRenderRequiredState = !singleMchd && requiresMchd;

  const {
    isPending: isPendingSendMchdInvite,
    mutate: mutateSendMchdInvite
  } = useMutation({
    mutationFn: sendMchdInvite,
    mutationKey: ['sendMchdInvite', location.pathname, orgId],
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Не удалось отправить инструкцию'));
    },
    onSuccess: () => {
      setIsInstructionSent(true);
      setResendSecondsLeft(RESEND_DELAY_SECONDS);
    }
  });

  useEffect(() => {
    if (!resendSecondsLeft) return;

    const intervalId = window.setInterval(() => {
      setResendSecondsLeft((currentValue) => Math.max(currentValue - 1, 0));
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [resendSecondsLeft]);

  const onClickActiveDetails = (): void => {
    setIsActiveDetailsOpen((currentValue) => !currentValue);
  };

  const onClickDetails = (): void => {
    setIsDetailsOpen((currentValue) => !currentValue);
  };

  const onClickSendInstruction = (): void => {
    if (resendSecondsLeft || isPendingSendMchdInvite) return;

    if (!orgId) {
      toast.error('Не удалось отправить инструкцию');
      return;
    }

    mutateSendMchdInvite({
      email: email || null,
      orgId
    });
  };

  if (isLoading) {
    return (
      <article className="flex min-h-[88px] items-center justify-center p-4 rounded-2xl border border-border-strong bg-surface">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </article>
    );
  }

  if (visibleMchdItems.length) {
    return (
      <article className="flex flex-col gap-2 p-[17px] rounded-2xl border border-border-strong bg-surface">
        <div className="flex items-center gap-2">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-[#13100d0d] dark:bg-[#ffffff0d]">
            <QrCode className="size-4 text-foreground" />
          </div>
          <h3 className="font-sans text-[16px] font-semibold leading-5 tracking-normal text-foreground">
            МЧД для Честного ЗНАКа
          </h3>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex w-full items-start gap-3 py-2">
            <div className="flex min-w-0 flex-1 flex-col gap-1 text-sm font-normal leading-5">
              <p className={cn(isMchdExpired || isMchdRevoked ? 'text-muted-foreground-soft' : 'text-foreground')}>
                № {singleMchd.id}
              </p>
              <p
                className={cn(
                  isMchdExpired || isMchdRevoked ? 'text-muted-foreground-soft' : 'text-muted-foreground'
                )}
              >
                Действует до:
                {isMchdRevoked
                  ? null
                  : ` ${formatDate({
                      fallback: singleMchd.end_date,
                      value: singleMchd.end_date,
                      variant: 'date'
                    })}`}
              </p>
            </div>

            {isMchdExpired ? (
              <div className="flex shrink-0 items-center justify-center gap-1 px-2 py-1 text-xs font-normal leading-4 rounded-full border border-border-strong bg-destructive/10 text-muted-foreground">
                <CircleAlert className="size-4 text-destructive" />
                <span>Истекла</span>
              </div>
            ) : isMchdRevoked ? (
              <div className="flex shrink-0 items-center justify-center gap-1 px-2 py-1 text-xs font-normal leading-4 rounded-full border border-border-strong bg-destructive/10 text-muted-foreground">
                <CircleAlert className="size-4 text-destructive" />
                <span>Отозвана</span>
              </div>
            ) : isMchdExpiresSoon ? (
              <div className="flex shrink-0 items-center justify-center gap-1 px-2 py-1 text-xs font-normal leading-4 rounded-full border border-border-strong bg-warning/10 text-muted-foreground">
                <CircleAlert className="size-4 text-warning" />
                <span>Истекает</span>
              </div>
            ) : (
              <div className="flex shrink-0 items-center justify-center gap-1 px-2 py-1 text-xs font-normal leading-4 rounded-full border border-border-strong bg-success/10 text-muted-foreground">
                <CheckCircle2 className="size-4 text-success" />
                <span>Активна</span>
              </div>
            )}
          </div>

          {shouldRenderInstructionBlock ? (
            isInstructionSent ? (
              <div className="flex flex-col gap-2 p-3 rounded-2xl border border-border-strong">
                <div className="flex items-start gap-2.5">
                  <MailCheck className="size-5 shrink-0 text-foreground" />
                  <p className="text-sm font-normal leading-5 text-foreground">Письмо отправлено</p>
                </div>
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-normal leading-5 text-info">{email}</p>
                    <p className="text-xs font-normal leading-4 text-muted-foreground-soft">
                      Откройте письмо на компьютере и следуйте инструкции для подписания МЧД через CryptoPro. Статус
                      обновится автоматически.
                    </p>
                  </div>
                  <Button
                    aria-disabled={Boolean(resendSecondsLeft) || isPendingSendMchdInvite}
                    className="w-full"
                    disabled={Boolean(resendSecondsLeft) || isPendingSendMchdInvite}
                    onClick={onClickSendInstruction}
                    type="button"
                  >
                    {isPendingSendMchdInvite ? (
                      <Loader2 className="size-5 animate-spin" />
                    ) : (
                      <MailCheck className="size-5" />
                    )}
                    <span>
                      {resendSecondsLeft ? `Отправить повторно через ${resendSecondsLeft} сек` : 'Отправить повторно'}
                    </span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-6 p-3 rounded-2xl border border-border-strong">
                <div className="flex flex-col gap-1 text-sm font-normal leading-5 text-muted-foreground">
                  <p className="text-info">{email}</p>
                  <p>
                    Мы отправим инструкцию и ссылку на указанную эл.почту. Откройте письмо на компьютере с
                    установленным CryptoPro и USB-ключом УКЭП и следуйте инструкции.
                  </p>
                </div>

                <Button
                  className="w-full"
                  disabled={isPendingSendMchdInvite}
                  onClick={onClickSendInstruction}
                  type="button"
                >
                  {isPendingSendMchdInvite ? (
                    <Loader2 className="size-5 animate-spin" />
                  ) : (
                    <MailCheck className="size-5" />
                  )}
                  <span>Получить инструкцию</span>
                </Button>
              </div>
            )
          ) : null}

          <div className="flex flex-col">
            <button
              className="flex w-fit items-center justify-center gap-1 py-2.5 text-sm font-semibold leading-5 text-link rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-expanded={isActiveDetailsOpen}
              onClick={onClickActiveDetails}
              type="button"
            >
              <span>Как отозвать МЧД?</span>
              <ChevronDown
                className={cn(
                  'size-5 shrink-0 transition-transform',
                  isActiveDetailsOpen && 'rotate-180'
                )}
              />
            </button>

            {isActiveDetailsOpen ? (
              <p className="text-xs font-normal leading-4 text-muted-foreground">
                Вы можете отозвать МЧД в любое время через личный кабинет ФНС, Госуслуги или Честный ЗНАК, без
                необходимости участия WhiteLine.
              </p>
            ) : null}
          </div>
        </div>
      </article>
    );
  }

  if (!shouldRenderRequiredState) {
    return null;
  }

  return (
    <article className="flex flex-col gap-2 p-[17px] rounded-2xl border border-border-strong bg-surface">
      <div className="flex items-center gap-2">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-[8px] bg-[#13100d0d] dark:bg-[#ffffff0d]">
          <QrCode className="size-4 text-foreground" />
        </div>
        <h3 className="font-sans text-[16px] font-semibold leading-5 tracking-normal text-foreground">
          МЧД для Честного ЗНАКа
        </h3>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex w-full items-start gap-3 py-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1 text-sm font-normal leading-5">
            <p className="text-foreground">Оформите МЧД</p>
          </div>
          <div className="flex shrink-0 items-center justify-center gap-1 px-2 py-1 text-xs font-normal leading-4 rounded-full border border-border-strong bg-destructive/10 text-muted-foreground">
            <CircleAlert className="size-4 text-destructive" />
            <span>Требуется</span>
          </div>
        </div>

        {isInstructionSent ? (
          <div className="flex flex-col gap-2 p-3 rounded-2xl border border-border-strong">
            <div className="flex items-start gap-2.5">
              <MailCheck className="size-5 shrink-0 text-foreground" />
              <p className="text-sm font-normal leading-5 text-foreground">Письмо отправлено</p>
            </div>
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-normal leading-5 text-info">{email}</p>
                <p className="text-xs font-normal leading-4 text-muted-foreground-soft">
                  Откройте письмо на компьютере и следуйте инструкции для подписания МЧД через CryptoPro. Статус
                  обновится автоматически.
                </p>
              </div>
              <Button
                aria-disabled={Boolean(resendSecondsLeft) || isPendingSendMchdInvite}
                className="w-full"
                disabled={Boolean(resendSecondsLeft) || isPendingSendMchdInvite}
                onClick={onClickSendInstruction}
                type="button"
              >
                {isPendingSendMchdInvite ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <MailCheck className="size-5" />
                )}
                <span>
                  {resendSecondsLeft ? `Отправить повторно через ${resendSecondsLeft} сек` : 'Отправить повторно'}
                </span>
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6 p-3 rounded-2xl border border-border-strong">
            <div className="flex flex-col gap-1 text-sm font-normal leading-5 text-muted-foreground">
              <p className="text-info">{email}</p>
              <p>
                Мы отправим инструкцию и ссылку на указанную эл.почту. Откройте письмо на компьютере с установленным
                CryptoPro и USB-ключом УКЭП и следуйте инструкции.
              </p>
            </div>

            <Button
              className="w-full"
              disabled={isPendingSendMchdInvite}
              onClick={onClickSendInstruction}
              type="button"
            >
              {isPendingSendMchdInvite ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <MailCheck className="size-5" />
              )}
              <span>Получить инструкцию</span>
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <button
            className="flex w-fit items-center gap-1 py-2.5 text-sm font-semibold leading-5 text-link focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-expanded={isDetailsOpen}
            onClick={onClickDetails}
            type="button"
          >
            <span>Подробнее</span>
            <ChevronDown
              className={cn(
                'size-5 shrink-0 transition-transform',
                isDetailsOpen && 'rotate-180'
              )}
            />
          </button>

          {isDetailsOpen ? (
            <ul className="flex flex-col gap-2">
              <li className="ml-[21px] list-disc text-sm font-normal leading-5 text-foreground marker:text-foreground">
                Не нужно маркировать самому
                <p className="mt-1 text-xs font-normal leading-5 text-muted-foreground">
                  Наши специалисты нанесут коды "Честный ЗНАК" на товары
                </p>
              </li>
              <li className="ml-[21px] list-disc text-sm font-normal leading-5 text-foreground marker:text-foreground">
                Работает по доверенности
                <p className="mt-1 text-xs font-normal leading-5 text-muted-foreground">
                  Вы выдаете электронную доверенность - мы действуем от вашего имени в системе "Честный ЗНАК"
                </p>
              </li>
              <li className="ml-[21px] list-disc text-sm font-normal leading-5 text-foreground marker:text-foreground">
                Контроль в ваших руках
                <p className="mt-1 text-xs font-normal leading-5 text-muted-foreground">
                  МЧД можно отозвать в любой момент через личный кабинет ФНС, Госуслуги или Честного ЗНАКа - без
                  участия WhiteLine.
                </p>
              </li>
            </ul>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default MchdCard;